/**
 * Next-session label resolver — shared source of truth for the admin
 * Operations Board card meta row. Uses the same session data the public
 * class detail page renders (ClassSession[]) and the canonical Academic
 * Calendar for semester date ranges.
 *
 * Selection rules:
 *   1. Active session containing today
 *   2. Otherwise, the next upcoming configured session
 *   3. Otherwise "No upcoming session"
 *
 * Ignores sessions whose dates cannot be resolved to a real window (pure
 * interest/coming-soon placeholders), sessions explicitly marked cancelled,
 * and past sessions.
 *
 * Externally managed classes short-circuit to "Privately managed" — they
 * don't require a Hub session.
 */

import type { ClassSession } from "@/data/classes";
import { parseTime, parseWeekdays, type DayName } from "./sessionSchedule";
import {
  getSemesterWindowForWeekday,
  semesterIdFromLabel,
  formatShortDate,
  type WeekdayName,
} from "./academicCalendar";

const MONTHS: Record<string, number> = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
  apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
  aug: 7, august: 7, sep: 8, sept: 8, september: 8,
  oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
};

const SHORT_MONTH = [
  "Jan", "Feb", "Mar", "Apr", "May", "June",
  "July", "Aug", "Sept", "Oct", "Nov", "Dec",
];

function fmt(d: Date): string {
  return `${SHORT_MONTH[d.getMonth()]} ${d.getDate()}`;
}

function fmtRange(a: Date, b: Date): string {
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return `${SHORT_MONTH[a.getMonth()]} ${a.getDate()}–${b.getDate()}`;
  }
  return `${fmt(a)} – ${fmt(b)}`;
}

/**
 * Try to parse a start/end date pair out of a free-form session `dates`
 * string. Returns null when nothing parseable is found.
 */
export function parseDateRange(text: string, refYear: number): { start: Date; end: Date } | null {
  if (!text) return null;
  const cleaned = text.replace(/[–—]/g, "-");
  const tokens: Array<{ month: number; day: number; year: number }> = [];
  const combined = /\b([A-Za-z]{3,9})\s+(\d{1,2})(?:,\s*(\d{4}))?|,\s*(\d{1,2})(?:,\s*(\d{4}))?/g;
  let currentMonth: number | null = null;
  let lastYear = refYear;
  let m: RegExpExecArray | null;
  while ((m = combined.exec(cleaned)) !== null) {
    if (m[1]) {
      const mo = MONTHS[m[1].toLowerCase()];
      if (mo === undefined) continue;
      currentMonth = mo;
      if (m[3]) lastYear = parseInt(m[3], 10);
      tokens.push({
        month: mo,
        day: parseInt(m[2], 10),
        year: m[3] ? parseInt(m[3], 10) : lastYear,
      });
    } else if (m[4] && currentMonth !== null) {
      if (m[5]) lastYear = parseInt(m[5], 10);
      tokens.push({
        month: currentMonth,
        day: parseInt(m[4], 10),
        year: m[5] ? parseInt(m[5], 10) : lastYear,
      });
    }
  }
  if (tokens.length === 0) return null;
  // If the first token got refYear but a later token has an explicit year, adopt it.
  if (tokens.length >= 2 && tokens[0].year === refYear && tokens[tokens.length - 1].year !== refYear) {
    tokens[0].year = tokens[tokens.length - 1].year;
  }
  const first = tokens[0];
  const last = tokens[tokens.length - 1];
  const start = new Date(first.year, first.month, first.day);
  const end = new Date(last.year, last.month, last.day);
  if (end < start) return null;
  return { start, end };
}

/** Widest semester window across a set of weekdays, using the academic calendar. */
export function semesterRangeForWeekdays(
  semesterId: string,
  weekdays: DayName[],
): { start: Date; end: Date } | null {
  const days: WeekdayName[] = weekdays.length
    ? (weekdays as WeekdayName[])
    : (["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"] as WeekdayName[]);
  let start: Date | null = null;
  let end: Date | null = null;
  for (const d of days) {
    const win = getSemesterWindowForWeekday(semesterId, d);
    if (!win) continue;
    const s = new Date(win.start + "T00:00:00");
    const e = new Date(win.end + "T00:00:00");
    if (!start || s < start) start = s;
    if (!end || e > end) end = e;
  }
  if (!start || !end) return null;
  return { start, end };
}

export interface ResolvedSession {
  label: string;
  /** "Session 3 · Jul 6–31", "Fall Semester · Sept 4 – Nov 20", etc. */
  display: string;
  state: "current" | "future";
}

export interface ResolveInput {
  sessions: ClassSession[] | null | undefined;
  scheduleText: string | null | undefined;
  managementType?: "silver_sage" | "external_provider" | null;
  visibilityStatus?: string | null;
}

export function resolveNextSessionLabel(input: ResolveInput, today: Date = new Date()): string {
  if (input.managementType === "external_provider") return "Privately managed";

  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const fallbackDays = parseWeekdays(input.scheduleText ?? "");
  const sessions = input.sessions ?? [];

  const resolved: ResolvedSession[] = [];
  for (const s of sessions) {
    if ((s as { cancelled?: boolean }).cancelled === true) continue;
    // A session explicitly closed by an admin is no longer operationally
    // current, even while today still falls inside its calendar window.
    if (s.closed === true) continue;
    const label = s.label ?? "";

    // 1. Parse literal dates from the session's own text.
    let range = parseDateRange(s.dates ?? "", t0.getFullYear());

    // 2. Fall back to the academic calendar for semester-labelled sessions.
    if (!range) {
      const semesterId = semesterIdFromLabel(`${label} ${s.dates ?? ""}`);
      if (semesterId) {
        const sessionDays = parseWeekdays(s.dates ?? "");
        const days = sessionDays.length ? sessionDays : fallbackDays;
        range = semesterRangeForWeekdays(semesterId, days);
      }
    }

    if (!range) continue; // interest-only / "dates coming soon" — skip
    if (range.end < t0) continue; // past

    const state: "current" | "future" = range.start <= t0 ? "current" : "future";
    const isSingle =
      range.start.getFullYear() === range.end.getFullYear() &&
      range.start.getMonth() === range.end.getMonth() &&
      range.start.getDate() === range.end.getDate();
    const rangeText = isSingle ? fmt(range.start) : fmtRange(range.start, range.end);
    const display = label ? `${label} · ${rangeText}` : rangeText;
    resolved.push({ label, display, state });

    // stash start for sorting
    (resolved[resolved.length - 1] as ResolvedSession & { _start: Date })._start = range.start;
  }

  const current = resolved.find((r) => r.state === "current");
  if (current) return current.display;

  const future = resolved
    .filter((r) => r.state === "future")
    .sort((a, b) => {
      const as = (a as ResolvedSession & { _start: Date })._start.getTime();
      const bs = (b as ResolvedSession & { _start: Date })._start.getTime();
      return as - bs;
    });
  if (future[0]) return future[0].display;

  const status = (input.visibilityStatus ?? "").toLowerCase();
  if (status === "coming_soon") return "Coming soon";
  return "No upcoming session";
}

/* -------------------------------------------------------------------------
 * Full session enumeration (admin surfaces)
 * -----------------------------------------------------------------------*/

export interface SessionOption {
  label: string;
  /** "August Session 4 (Aug 3 – Aug 28)" */
  display: string;
  /** "Aug 3 – Aug 28" or "" when the session has no resolvable dates. */
  rangeText: string;
  state: "past" | "current" | "future" | "undated";
  start: Date | null;
  end: Date | null;
}

/**
 * Every configured session for a class, resolved to real date windows where
 * possible, sorted current → future → undated → past (most recent first).
 * Same parsing rules as `resolveNextSessionLabel` — no separate logic.
 */
export function listClassSessionOptions(
  input: ResolveInput,
  today: Date = new Date(),
): SessionOption[] {
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const fallbackDays = parseWeekdays(input.scheduleText ?? "");
  const out: SessionOption[] = [];

  for (const s of input.sessions ?? []) {
    if ((s as { cancelled?: boolean }).cancelled === true) continue;
    const label = (s.label ?? "").trim();
    if (!label) continue;

    let range = parseDateRange(s.dates ?? "", t0.getFullYear());
    if (!range) {
      const semesterId = semesterIdFromLabel(`${label} ${s.dates ?? ""}`);
      if (semesterId) {
        const sessionDays = parseWeekdays(s.dates ?? "");
        range = semesterRangeForWeekdays(semesterId, sessionDays.length ? sessionDays : fallbackDays);
      }
    }

    // Admin-closed sessions stay in the list as HISTORICAL buckets (payroll,
    // registrations, reporting still resolve against them) but can never be
    // the operational anchor — `resolveNextSessionOption` skips `past`.
    const closed = s.closed === true;

    if (!range) {
      out.push({
        label,
        display: label,
        rangeText: "",
        state: closed ? "past" : "undated",
        start: null,
        end: null,
      });
      continue;
    }

    const isSingle =
      range.start.getFullYear() === range.end.getFullYear() &&
      range.start.getMonth() === range.end.getMonth() &&
      range.start.getDate() === range.end.getDate();
    const rangeText = isSingle ? fmt(range.start) : fmtRange(range.start, range.end);
    const state: SessionOption["state"] = closed
      ? "past"
      : range.end < t0 ? "past" : range.start <= t0 ? "current" : "future";
    out.push({
      label,
      display: `${label} (${rangeText})`,
      rangeText,
      state,
      start: range.start,
      end: range.end,
    });
  }

  const rank = { current: 0, future: 1, undated: 2, past: 3 } as const;
  return out.sort((a, b) => {
    if (rank[a.state] !== rank[b.state]) return rank[a.state] - rank[b.state];
    const at = a.start?.getTime() ?? 0;
    const bt = b.start?.getTime() ?? 0;
    return a.state === "past" ? bt - at : at - bt;
  });
}

/**
 * The class's canonical CURRENT session (in progress today), else the soonest
 * FUTURE session, else an undated session. Past sessions are never returned.
 * This is the anchor operational surfaces must use before choosing an
 * instructor assignment — a historical session label must never become the
 * card's session just because an assignment row is still marked active.
 */
export function resolveNextSessionOption(
  input: ResolveInput,
  today: Date = new Date(),
): SessionOption | null {
  const options = listClassSessionOptions(input, today);
  return (
    options.find((o) => o.state === "current") ??
    options.find((o) => o.state === "future") ??
    options.find((o) => o.state === "undated") ??
    null
  );
}
